import { useEffect, useMemo, useRef, useState } from 'react';
import { Assets, Container as ContainerType } from 'pixi.js';
import { Container, Sprite } from '@pixi/react';
import { observer } from 'mobx-react-lite';

import { jackpotIcons, jackPotItemsHelper, jackPotSymbols } from '../helpers/jackpotSceneHelper';
import { soundService, SoundTypes } from '../../../../services/soundService';
import { JackPotSceneNumberItemWrapper } from './jackPotNumberItem';
import { JackPotSceneItemWrapper } from './jackPotSceneItem';
import { FullScreenComponent } from './fullScreenComponent';
import { JackpotBackGround } from './jackpotBackGround';
import { JackPotExpireDate } from './jackPotExpireDate';
import { ScaleComponent } from '../../../components';
import JackPotWinPopup from './jackPotWinPopup';
import { isMobile } from '../../../../helpers';
import { useStore } from '../../../../hooks';
import { Spine } from '../../../components';

export const JackpotScene: React.FC = observer(() => {

  const [ready, setReady] = useState(false);
  const [mobileBackgroundSize, setMobileBackgroundSize] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  const [isLandscape, setIsLandscape] = useState(() => window.innerWidth > window.innerHeight);

  const jackpotGameSceneBoardRef = useRef<ContainerType | any>();
  const jackpotBackgroundRef = useRef<ContainerType | any>();
  const symbolMoveContainerRef = useRef();

  const { jackpotStore, gameStore } = useStore();
  const { openJackpotScene, jackPotData, jackpot, shouldPlayInitialJackpotMusic } = jackpotStore;
  const { soundEnabled } = gameStore;
  const { jackPotItemEnum, jackpotBg } = jackPotItemsHelper()

  const winIcon = jackPotData?.find((el) => el?.isWin);

  useEffect(() => {
    if (!jackpotGameSceneBoardRef.current || !openJackpotScene) return;

    const jackpotAmountBoardSpine = new Spine({ key: 'jackpotGameSceneBoard' });
    jackpotAmountBoardSpine.play('idle', true);
    jackpotGameSceneBoardRef.current.addChild(jackpotAmountBoardSpine);
  }, [jackpotGameSceneBoardRef, openJackpotScene]);

  useEffect(() => {
    if (jackpot.name !== '') {
      jackpotStore.openJackpotSceneAmountSetter()
    }
  }, [jackpot])

  useEffect(() => {
    if (!shouldPlayInitialJackpotMusic || !soundEnabled) return;
    soundService.stop(SoundTypes.JackpotBackgroundMusic);
    soundService.play(SoundTypes.JackpotBackgroundMusic, { loop: true });
    jackpotStore.setShouldPlayInitialJackpotMusic(false);
  }, [shouldPlayInitialJackpotMusic, soundEnabled]);

  useEffect(() => {
    if (winIcon && jackpotGameSceneBoardRef.current) {

      jackpotGameSceneBoardRef.current?.children[0].play(
        `win_${jackpotIcons[winIcon.jackpotCard]}`,
        true
      );
    };
  }, [winIcon]);

  useEffect(() => {
    Assets.load('frame/frameModuleResources/main/bitmapFonts/numbersJackpot.xml').then(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!isMobile()) return;

    const updateOrientation = () => {
      const currentLandscape = window.innerWidth > window.innerHeight;
      setIsLandscape(currentLandscape);
      if (currentLandscape) {
        setMobileBackgroundSize({ width: 960, height: 540 });
      } else {
        setMobileBackgroundSize({ width: window.innerWidth, height: window.innerHeight });
      }
    };

    updateOrientation();
    window.addEventListener('resize', updateOrientation);
    window.addEventListener('orientationchange', updateOrientation);

    return () => {
      window.removeEventListener('resize', updateOrientation);
      window.removeEventListener('orientationchange', updateOrientation);
    };
  }, []);

  const mobileBackground = useMemo(() => {
    if (isLandscape) return null;

    return (
      <>
        <FullScreenComponent>
          <Sprite
            anchor={0.5}
            texture={jackpotBg}
            width={mobileBackgroundSize.width}
            height={mobileBackgroundSize.height}
          />
        </FullScreenComponent>
        <JackpotBackGround />
      </>
    );
  }, [jackpotBg, isLandscape, mobileBackgroundSize.height, mobileBackgroundSize.width]);

  return (
    !openJackpotScene ? null :
      <Container {...({ name: 'JackPotSceneContainer' } as any)}>
        <Container
          x={isMobile() ? 0 : 720}
          y={isMobile() ? 0 : 410}
          ref={jackpotBackgroundRef}
          {...({ name: 'JackpotGameSceneBoardContainer' } as any)}
          interactive={true}
        >
          {isMobile() ? (
            mobileBackground
          ) : (
            <>
              <Sprite texture={jackpotBg} anchor={0.5} />
              <Container ref={jackpotGameSceneBoardRef} />
            </>
          )}
        </Container>
        {
          <ScaleComponent isCenterHorizontal>
            <Container  {...({ name: 'JackpotGameSceneIconsContainer' } as any)}>
              <JackPotSceneNumberItemWrapper winIcon={winIcon} jackPotItemEnum={jackPotItemEnum} />
              <JackPotSceneItemWrapper
                jackpotIcons={jackpotIcons}
                jackPotSymbols={jackPotSymbols}
                jackPotItemEnum={jackPotItemEnum}
                symbolMoveContainerRef={symbolMoveContainerRef}
              />
              <Container ref={symbolMoveContainerRef} {...({ name: 'JackPotSymbolMoveContainer' } as any)} />
              <JackPotExpireDate x={isMobile() ? 268 : 715} y={isMobile() ? 110 : 140} />
              {winIcon && ready && <JackPotWinPopup winIcon={winIcon} jackpotIcons={jackpotIcons} />}
            </Container>
          </ScaleComponent>
        }
      </Container>
  );
});
